// manifest 表单：按任务类型的 manifestSchema 生成初值、逐项校验（与后端 validateManifest 规则一致）、转成提交数据
import type { ManifestField, ManifestSchema } from "@csp/shared";

export type FieldValue = string | boolean;
export type FormValues = Record<string, FieldValue>;

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === "string" && v.trim() === "");
}

/** 初值：已有 manifest 的值优先，boolean 默认 false，其余空串 */
export function initialValues(schema: ManifestSchema, existing: Record<string, unknown> = {}): FormValues {
  const out: FormValues = {};
  for (const f of schema.fields) {
    const v = existing[f.key];
    if (f.type === "boolean") out[f.key] = v === true || v === "true";
    else out[f.key] = isEmpty(v) ? "" : String(v);
  }
  return out;
}

/** 单个字段校验，返回错误文案（null = 通过） */
export function validateField(f: ManifestField, v: FieldValue | undefined): string | null {
  if (f.type === "boolean") return null;
  const s = typeof v === "string" ? v.trim() : "";
  if (!s) return f.required ? "必填" : null;
  if (f.type === "enum" && f.options && !f.options.includes(s)) return "请从列表里选择";
  if (f.type === "number" && Number.isNaN(Number(s))) return "应为数字";
  if (f.type === "date" && Number.isNaN(Date.parse(s))) return "日期无效";
  if (f.pattern) {
    let re: RegExp | null = null;
    try {
      re = new RegExp(f.pattern);
    } catch {
      re = null;
    }
    if (re && !re.test(s)) return f.hint ? `格式不对（${f.hint}）` : "格式不对";
  }
  return null;
}

export function validateAll(schema: ManifestSchema, values: FormValues): Record<string, string> {
  const errs: Record<string, string> = {};
  for (const f of schema.fields) {
    const e = validateField(f, values[f.key]);
    if (e) errs[f.key] = e;
  }
  return errs;
}

/** 提交数据：去空格、数字转 number、空的可选字段省略 */
export function toManifest(schema: ManifestSchema, values: FormValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of schema.fields) {
    const v = values[f.key];
    if (f.type === "boolean") {
      out[f.key] = v === true;
      continue;
    }
    const s = typeof v === "string" ? v.trim() : "";
    if (!s) continue;
    out[f.key] = f.type === "number" ? Number(s) : s;
  }
  return out;
}

/** 必备文件是否已具备：已上传（synced）或本次已选择 */
export function missingFiles(
  schema: ManifestSchema,
  uploaded: { key: string; state: string }[],
  selected: Record<string, File[]>,
): string[] {
  return schema.files
    .filter((r) => r.required)
    .filter((r) => !uploaded.some((f) => f.key === r.key && f.state === "synced") && !(selected[r.key]?.length))
    .map((r) => r.label);
}

/** <input accept> 属性：["pdf","jpg"] → ".pdf,.jpg" */
export function acceptAttr(accept?: string[]): string | undefined {
  // 扩展名补点（pdf → .pdf）；MIME（application/pdf、image/*）原样
  return accept?.length ? accept.map((a) => (a.includes("/") || a.startsWith(".") ? a : `.${a}`)).join(",") : undefined;
}
